import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { ROLE_LABELS } from "../../lib/nav";
import WhatsAppLink from "../../components/WhatsAppLink";
import PageHeader from "../../components/PageHeader";
import DateInput from "../../components/DateInput";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import { UserCog, Plus, Loader2, ShieldCheck, Trash2, Power, Pencil, KeyRound, Camera } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import MultiCellulare from "../../components/MultiCellulare";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";
import { toast } from "sonner";

const ROLE_BADGE = {
  superadmin: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
  titolare: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  amministratore: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  operatore: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cliente: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};
const EDIT_ROLES = ["titolare", "amministratore", "operatore", "cliente"];

const EMPTY_USER = {
  nome: "", cognome: "", data_nascita: "", codice_fiscale: "",
  indirizzo: "", comune: "", cap: "", provincia: "",
  cellulare: "", cellulari_extra: [], cellulari_etichette: [], telefono: "", email: "", otp_email: "", pec: "", note: "",
  role: "operatore", status: "active", password: "",
};

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&";
  const arr = new Uint32Array(14);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join("");
};

const inputCls = "mt-1.5 bg-slate-900/60 border-slate-700";
const SectionTitle = ({ children }) => (
  <div className="text-[11px] uppercase tracking-wider font-mono text-amber-500/80 border-b border-slate-800 pb-1.5 mb-3">{children}</div>
);

export function UserFormFields({ form, onField, showPassword, prefix }) {
  const pickAddr = (p) => {
    onField("indirizzo", p.indirizzo);
    onField("comune", p.comune);
    onField("cap", p.cap);
    onField("provincia", p.provincia);
  };
  return (
    <div className="space-y-6">
      <div>
        <SectionTitle>Dati personali</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300">Cognome</Label>
            <Input value={form.cognome} onChange={(e) => onField("cognome", e.target.value)} data-testid={`${prefix}-cognome`} className={inputCls} />
          </div>
          <div>
            <Label className="text-slate-300">Nome</Label>
            <Input value={form.nome} onChange={(e) => onField("nome", e.target.value)} data-testid={`${prefix}-nome`} className={inputCls} />
          </div>
          <div>
            <Label className="text-slate-300">Data di nascita</Label>
            <div className="mt-1.5">
              <DateInput value={form.data_nascita} onChange={(v) => onField("data_nascita", v)} testId={`${prefix}-data-nascita`} className="bg-slate-900/60 border-slate-700" />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Codice Fiscale</Label>
            <Input value={form.codice_fiscale} onChange={(e) => onField("codice_fiscale", e.target.value.toUpperCase())} data-testid={`${prefix}-codice-fiscale`} className={`${inputCls} font-mono uppercase`} />
          </div>
          <div className="col-span-2">
            <Label className="text-slate-300">Indirizzo di residenza</Label>
            <div className="mt-1.5">
              <AddressAutocomplete
                value={form.indirizzo}
                onChange={(v) => onField("indirizzo", v)}
                onPick={pickAddr}
                city={form.comune}
                prov={form.provincia}
                testId={`${prefix}-indirizzo`}
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Comune</Label>
            <Input value={form.comune} onChange={(e) => onField("comune", e.target.value)} data-testid={`${prefix}-comune`} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">CAP</Label>
              <Input value={form.cap} onChange={(e) => onField("cap", e.target.value)} data-testid={`${prefix}-cap`} className={inputCls} />
            </div>
            <div>
              <Label className="text-slate-300">Provincia</Label>
              <Input value={form.provincia} onChange={(e) => onField("provincia", e.target.value.toUpperCase())} maxLength={2} data-testid={`${prefix}-provincia`} className={`${inputCls} uppercase`} />
            </div>
          </div>
        </div>
      </div>

      <div>
        <SectionTitle>Recapiti</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300 inline-flex items-center gap-1.5">Cellulare<WhatsAppLink number={form.cellulare} size="h-3.5 w-3.5" testId={`${prefix}-cellulare-wa`} /></Label>
            <MultiCellulare extra={form.cellulari_extra} onExtra={(v) => onField("cellulari_extra", v)} labels={form.cellulari_etichette} onLabels={(v) => onField("cellulari_etichette", v)} testId={`${prefix}-cellulare`}>
            <Input value={form.cellulare} onChange={(e) => onField("cellulare", e.target.value)} data-testid={`${prefix}-cellulare`} className={inputCls} />
            </MultiCellulare>
          </div>
          <div>
            <Label className="text-slate-300">Telefono</Label>
            <Input value={form.telefono} onChange={(e) => onField("telefono", e.target.value)} data-testid={`${prefix}-telefono`} className={inputCls} />
          </div>
          <div>
            <Label className="text-slate-300">Accesso tramite email</Label>
            <Input type="email" value={form.email} onChange={(e) => onField("email", e.target.value)} data-testid={`${prefix}-email`} className={inputCls} />
          </div>
          <div>
            <Label className="text-slate-300">Email per ricezione OTP</Label>
            <Input type="email" value={form.otp_email} placeholder="Se vuota usa l'email di login" onChange={(e) => onField("otp_email", e.target.value)} data-testid={`${prefix}-otp-email`} className={inputCls} />
          </div>
          <div className="col-span-2">
            <Label className="text-slate-300">PEC</Label>
            <Input type="email" value={form.pec || ""} onChange={(e) => onField("pec", e.target.value)} data-testid={`${prefix}-pec`} className={inputCls} />
          </div>
        </div>
      </div>

      <div>
        <SectionTitle>Note</SectionTitle>
        <textarea value={form.note || ""} onChange={(e) => { onField("note", e.target.value); e.target.style.height = "auto"; e.target.style.height = `${e.target.scrollHeight}px`; }}
          rows={3} placeholder="Annotazioni libere…" data-testid={`${prefix}-note`}
          className="w-full min-h-[80px] resize-y rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-500/60" />
      </div>

      <div>
        <SectionTitle>Accesso</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300">Ruolo</Label>
            <Select value={form.role} onValueChange={(v) => onField("role", v)}>
              <SelectTrigger data-testid={`${prefix}-role`} className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                {EDIT_ROLES.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`${prefix}-role-opt-${r}`}>{ROLE_LABELS[r]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Stato</Label>
            <div className="mt-3 flex items-center gap-2">
              <Switch checked={form.status === "active"}
                onCheckedChange={(v) => onField("status", v ? "active" : "suspended")}
                data-testid={`${prefix}-status`} className="data-[state=checked]:bg-emerald-500" />
              <span className="text-sm text-slate-300">{form.status === "active" ? "Attivo" : "Inattivo"}</span>
            </div>
          </div>
          {showPassword && (
            <div className="col-span-2">
              <Label className="text-slate-300">Password</Label>
              <div className="mt-1.5 flex gap-2">
                <Input value={form.password} onChange={(e) => onField("password", e.target.value)}
                  data-testid={`${prefix}-password`} className="bg-slate-900/60 border-slate-700 font-mono" placeholder="Min. 6 caratteri" />
                <Button type="button" variant="outline" onClick={() => onField("password", genPassword())}
                  data-testid={`${prefix}-gen-password`} className="border-slate-700 text-amber-400 hover:bg-amber-500/10 shrink-0">
                  <KeyRound className="h-4 w-4 mr-1" /> Genera
                </Button>
              </div>
              <p className="mt-1 text-[11px] text-slate-500">Comunica la password all'utente: verrà mostrata solo ora.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Operatori() {
  const [users, setUsers] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_USER);

  // permissions sheet
  const [permSheet, setPermSheet] = useState(false);
  const [selected, setSelected] = useState(null);
  const [draftPerms, setDraftPerms] = useState({});
  const [savingPerms, setSavingPerms] = useState(false);

  // edit dialog
  const [editUser, setEditUser] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [us, secs] = await Promise.all([api.get("/studio/utenti"), api.get("/sections")]);
      setUsers(us.data);
      setSections(secs.data.sections);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setFormField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setEditField = (k, v) => setEditUser((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setForm({ ...EMPTY_USER, password: genPassword() }); setOpen(true); };

  const create = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error("Inserisci l'email di accesso"); return; }
    if ((form.password || "").length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    setSaving(true);
    try {
      await api.post("/studio/utenti", form);
      toast.success("Utente creato");
      setOpen(false);
      setForm(EMPTY_USER);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openPerms = (op) => {
    setSelected(op);
    const base = {};
    sections.forEach((s) => {
      const v = op.permissions && op.permissions[s.key];
      base[s.key] = v === true ? "write" : (["none", "read", "write"].includes(v) ? v : "none");
    });
    setDraftPerms(base);
    setPermSheet(true);
  };

  const savePerms = async () => {
    setSavingPerms(true);
    try {
      const { data } = await api.patch(`/studio/operators/${selected.id}/permissions`, { permissions: draftPerms });
      toast.success("Permessi aggiornati");
      setUsers((prev) => prev.map((o) => (o.id === data.id ? data : o)));
      setPermSheet(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingPerms(false);
    }
  };

  const openEdit = (u) => {
    setEditUser({
      id: u.id,
      nome: u.nome || (u.name || "").split(" ").slice(1).join(" ") || "",
      cognome: u.cognome || (u.name || "").split(" ")[0] || "",
      data_nascita: u.data_nascita || "",
      codice_fiscale: u.codice_fiscale || "",
      indirizzo: u.indirizzo || "",
      comune: u.comune || "",
      cap: u.cap || "",
      provincia: u.provincia || "",
      cellulare: u.cellulare || "",
      telefono: u.telefono || "",
      email: u.email || "",
      otp_email: u.otp_email || "",
      pec: u.pec || "",
      cellulari_extra: u.cellulari_extra || [],
      cellulari_etichette: u.cellulari_etichette || [],
      note: u.note || "",
      role: u.role,
      status: u.status || "active",
    });
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const { password, id, ...payload } = editUser;
      await api.patch(`/studio/utenti/${id}`, payload);
      toast.success("Utente aggiornato");
      setEditUser(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  const [sendingFoto, setSendingFoto] = useState(false);
  const sendFotoLink = async () => {
    setSendingFoto(true);
    try {
      const { data } = await api.post(`/studio/utenti/${editUser.id}/invia-link-foto`);
      toast.success(`Link area foto inviato a ${data.email}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSendingFoto(false);
    }
  };

  const toggleFoto = async (op, enabled) => {
    try {
      await api.patch(`/studio/operators/${op.id}/foto-access`, { enabled });
      setUsers((prev) => prev.map((o) => (o.id === op.id ? { ...o, foto_enabled: enabled } : o)));
      toast.success(enabled ? "Accesso pagina Foto abilitato" : "Accesso pagina Foto disabilitato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleStatus = async (op) => {
    const next = op.status === "active" ? "suspended" : "active";
    try {
      await api.patch(`/studio/operators/${op.id}/status`, { status: next });
      toast.success(next === "active" ? "Utente riattivato" : "Utente sospeso");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (op) => {
    if (!window.confirm(`Eliminare l'operatore ${op.name}?`)) return;
    try {
      await api.delete(`/studio/operators/${op.id}`);
      toast.success("Operatore eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const countPerms = (op) => sections.filter((s) => {
    const v = op.permissions && op.permissions[s.key];
    return v === true || v === "read" || v === "write";
  }).length;

  return (
    <div data-testid="operatori-page">
      <PageHeader
        title="Gestione Utenti"
        subtitle="Gestisci i ruoli e i permessi degli utenti del tuo studio."
        icon={UserCog}
        actions={
          <Button onClick={openCreate} data-testid="new-user-button" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            <Plus className="h-4 w-4 mr-1" /> Nuovo Utente
          </Button>
        }
      />

      <div className="pgp-card overflow-x-auto pgp-fade-up">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">Nessun utente.</div>
        ) : (
          <table className="w-full text-sm" data-testid="operatori-table">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wider font-mono border-b border-slate-800">
                <th className="px-5 py-3">Utente</th>
                <th className="px-5 py-3">Ruolo</th>
                <th className="px-5 py-3">Permessi</th>
                <th className="px-5 py-3">Creato</th>
                <th className="px-5 py-3">Stato</th>
                <th className="px-5 py-3">Pagina Foto</th>
                <th className="px-5 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {users.map((op) => (
                <tr key={op.id} className="hover:bg-slate-800/40 transition-colors" data-testid={`operator-row-${op.id}`}>
                  <td className="px-5 py-3.5 font-medium text-slate-100">
                    {op.name}
                    <div className="text-[11px] text-slate-500 font-mono">{op.email}</div>
                  </td>
                  <td className="px-5 py-3.5">
                    <Badge className={`border ${ROLE_BADGE[op.role] || ROLE_BADGE.operatore} font-mono`} data-testid={`user-role-${op.id}`}>
                      {ROLE_LABELS[op.role] || op.role}
                    </Badge>
                  </td>
                  <td className="px-5 py-3.5">
                    {op.role === "operatore" ? (
                      <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/15 font-mono">
                        {countPerms(op)}/{sections.length} sezioni
                      </Badge>
                    ) : (
                      <span className="text-slate-500 text-xs">Accesso completo</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-slate-400 font-mono text-xs">{formatDate(op.created_at)}</td>
                  <td className="px-5 py-3.5">
                    {op.status === "active" ? (
                      <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/15">Attivo</Badge>
                    ) : (
                      <Badge className="bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/15">Inattivo</Badge>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    {op.role === "operatore" ? (
                      <Switch checked={!!op.foto_enabled} onCheckedChange={(v) => toggleFoto(op, v)} title="Accesso pagina Foto (/foto)" data-testid={`foto-access-${op.id}`} />
                    ) : (
                      <span className="text-slate-500 text-xs" data-testid={`foto-access-always-${op.id}`}>Sempre</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(op)} data-testid={`edit-${op.id}`}
                      className="text-sky-400 hover:text-sky-300 hover:bg-sky-500/10">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {op.role === "operatore" && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openPerms(op)} data-testid={`perms-${op.id}`}
                          className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10">
                          <ShieldCheck className="h-4 w-4 mr-1" /> Permessi
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => toggleStatus(op)} data-testid={`status-${op.id}`}
                          className="text-slate-400 hover:text-slate-200 hover:bg-slate-700/40">
                          <Power className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(op)} data-testid={`delete-${op.id}`}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="new-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Nuovo utente</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} data-testid="new-user-form">
            <UserFormFields form={form} onField={setFormField} showPassword prefix="create" />
            <DialogFooter className="mt-6">
              <Button type="submit" disabled={saving} data-testid="submit-user-button"
                className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea utente"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Modifica utente</DialogTitle>
          </DialogHeader>
          {editUser && (
            <UserFormFields form={editUser} onField={setEditField} showPassword={false} prefix="edit" />
          )}
          <DialogFooter className="mt-6 gap-2">
            <Button variant="outline" onClick={sendFotoLink} disabled={sendingFoto} data-testid="send-foto-link-button" className="border-slate-700">
              {sendingFoto ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Camera className="h-4 w-4 mr-2" />} Invia link area foto
            </Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="save-user-button"
              className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva modifiche"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions sheet */}
      <Sheet open={permSheet} onOpenChange={setPermSheet}>
        <SheetContent className="bg-[#0d111a] border-slate-800 text-slate-100 w-full sm:max-w-md overflow-y-auto" data-testid="permissions-sheet">
          <SheetHeader>
            <SheetTitle className="font-head text-slate-50">Permessi granulari</SheetTitle>
            <SheetDescription className="text-slate-400">
              {selected?.name} — imposta il livello di accesso per ogni sezione. Le nuove sezioni vengono aggiunte automaticamente con accesso negato.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-2">
            {sections.map((s) => {
              const cur = draftPerms[s.key] || "none";
              const OPTS = [
                { v: "none", label: "Nessuno" },
                { v: "read", label: "Lettura" },
                { v: "write", label: "Lettura+Scrittura" },
              ];
              return (
                <div key={s.key} className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3"
                  data-testid={`perm-row-${s.key}`}>
                  <span className="text-sm text-slate-200">{s.label}</span>
                  <div className="inline-flex rounded-md overflow-hidden border border-slate-700 shrink-0">
                    {OPTS.map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setDraftPerms((p) => ({ ...p, [s.key]: o.v }))}
                        data-testid={`perm-${s.key}-${o.v}`}
                        className={`px-2.5 py-1 text-xs font-medium transition-colors ${cur === o.v ? "bg-amber-500 text-[#0a0d14]" : "bg-transparent text-slate-400 hover:bg-slate-800"}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <Button onClick={savePerms} disabled={savingPerms} data-testid="save-permissions-button"
            className="w-full mt-6 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {savingPerms ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva permessi"}
          </Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
